import {
  ActionViewModel,
  ActionViewModelImplementation,
  durationInSecondsOrZero,
  Effector,
  secondsToDuration,
  uuidOrNewUuid,
} from "./ActionViewModel";
import {
  allEffectorsWithType,
  DirectionToggle,
  StartDelayInput,
} from "./utils";
import { FormControl, FormLabel, InputAdornment } from "@mui/material";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { FormikNumberInput } from "common/components/FormikNumberInput";
import { functionNameForScvInputId } from "portal/utils/atkEquipment";
import {
  QuickAction,
  ScvDetentDown,
  ScvDetentUp,
  ScvFloat,
} from "protos/atk/activities";
import { Replace } from "common/utils/types";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TFunction } from "i18next";
import { useRandomId } from "portal/utils/hooks/useRandomId";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import ConstantPressureIcon from "common/images/atk-actions/constant-pressure.svg?react";
import DetentDownIcon from "common/images/atk-actions/detent-down.svg?react";
import DetentUpIcon from "common/images/atk-actions/detent-up.svg?react";
import FloatIcon from "common/images/atk-actions/float.svg?react";
import React from "react";
import TimedForceIcon from "common/images/atk-actions/timed-force.svg?react";

//#region SCV detent up
class ScvDetentUpViewModel extends ActionViewModel<ScvDetentUpViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string,
    public actionData: Replace<
      ScvDetentUp,
      { vtDetentTime: number /* seconds */ }
    >
  ) {
    super(effector, false, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      scvDetentUp: {
        ...this.actionData,
        vtDetentTime: secondsToDuration(this.actionData.vtDetentTime),
      },
      carryOverIntoWorkTask: false,
    };
  }

  override actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.scvDetentUp",
        {
          function: functionNameForScvInputId(
            t,
            rigEffectiveConfig,
            this.actionData.scvInputId
          ),
        }
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.detentUp"
    );
  }

  override components = scvDetentUp.components;
}
export const scvDetentUp: ActionViewModelImplementation<ScvDetentUpViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.detentUp",
    actionMenuSection: "quick-action",
    isNewActionAvailableForEffector: allEffectorsWithType("scv"),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "scv") {
        throw new Error('scvDetentUp.newAction: effector type is not "scv"');
      }

      return new ScvDetentUpViewModel(effector, startInStageIndex, 0, "", {
        scvInputId: effector.scvInputId,
        vtDetentTime: 0,
      });
    },
    fromQuickAction: (action, startInStageIndex) => {
      if (action.scvDetentUp) {
        return new ScvDetentUpViewModel(
          {
            type: "scv",
            scvInputId: action.scvDetentUp.scvInputId,
          },
          startInStageIndex,
          durationInSecondsOrZero(action.startDelay),
          action.actionId,
          {
            ...action.scvDetentUp,
            vtDetentTime: durationInSecondsOrZero(
              action.scvDetentUp.vtDetentTime
            ),
          }
        );
      }
    },
    components: {
      Icon: DetentUpIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const { measurementSystem } = useSelf();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormikDimensionedNumberInput
              name={basePath.actionData.vtDetentTime.$path}
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.vtDetentTime"
              )}
              dimensionedNumberInputProps={{
                minStoredValue: 0,
                storedUnit: "s",
                localSystem: measurementSystem,
                displayUnits: { imperial: "s", metric: "s" },
              }}
              className="w-full"
            />
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };
//#endregion

//#region SCV detent down
class ScvDetentDownViewModel extends ActionViewModel<ScvDetentDownViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string,
    public actionData: Replace<ScvDetentDown, { vtDetentTime: number }>
  ) {
    super(effector, false, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      scvDetentDown: {
        ...this.actionData,
        vtDetentTime: secondsToDuration(this.actionData.vtDetentTime),
      },
      carryOverIntoWorkTask: false,
    };
  }
  override actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.scvDetentDown",
        {
          function: functionNameForScvInputId(
            t,
            rigEffectiveConfig,
            this.actionData.scvInputId
          ),
        }
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.detentDown"
    );
  }

  override components = scvDetentDown.components;
}
export const scvDetentDown: ActionViewModelImplementation<ScvDetentDownViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.detentDown",
    actionMenuSection: "quick-action",
    isNewActionAvailableForEffector: allEffectorsWithType("scv"),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "scv") {
        throw new Error('scvDetentDown.newAction: effector type is not "scv"');
      }

      return new ScvDetentDownViewModel(effector, startInStageIndex, 0, "", {
        scvInputId: effector.scvInputId,
        vtDetentTime: 0,
      });
    },
    fromQuickAction: (action, startInStageIndex) => {
      if (action.scvDetentDown) {
        return new ScvDetentDownViewModel(
          {
            type: "scv",
            scvInputId: action.scvDetentDown.scvInputId,
          },
          startInStageIndex,
          durationInSecondsOrZero(action.startDelay),
          action.actionId,
          {
            ...action.scvDetentDown,
            vtDetentTime: durationInSecondsOrZero(
              action.scvDetentDown.vtDetentTime
            ),
          }
        );
      }
    },
    components: {
      Icon: DetentDownIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const { measurementSystem } = useSelf();
        return (
          <div className="grid grid-cols-2 gap-2">
            <FormikDimensionedNumberInput
              name={basePath.actionData.vtDetentTime.$path}
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.vtDetentTime"
              )}
              dimensionedNumberInputProps={{
                minStoredValue: 0,
                storedUnit: "s",
                localSystem: measurementSystem,
                displayUnits: { imperial: "s", metric: "s" },
              }}
              className="w-full"
            />
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };
//#endregion

//#region SCV timed force
class ScvTimedForceViewModel extends ActionViewModel<ScvTimedForceViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: {
      scvInputId: string;
      // separate out sign from the force in the view model so we can provide a
      // radio button for retract/extend
      direction: 1 | -1;
      forcePct: number;
      durationSec: number;
    }
  ) {
    super(effector, false, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      scvTimedForce: {
        scvInputId: this.actionData.scvInputId,
        forcePct: this.actionData.forcePct * this.actionData.direction,
        duration: secondsToDuration(this.actionData.durationSec),
      },
      carryOverIntoWorkTask: false,
    };
  }
  override actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.scvTimedForce",
        {
          function: functionNameForScvInputId(
            t,
            rigEffectiveConfig,
            this.actionData.scvInputId
          ),
        }
      );
    }
  }
  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.timedForce"
    );
  }
  override components = scvTimedForce.components;
}
export const scvTimedForce: ActionViewModelImplementation<ScvTimedForceViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.timedForce",
    actionMenuSection: "quick-action",
    isNewActionAvailableForEffector: allEffectorsWithType("scv"),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "scv") {
        throw new Error('scvTimedForce.newAction: effector type is not "scv"');
      }

      return new ScvTimedForceViewModel(
        effector,
        startInStageIndex,
        0,
        undefined,
        {
          scvInputId: effector.scvInputId,
          direction: 1,
          forcePct: 0,
          durationSec: 0,
        }
      );
    },
    fromQuickAction: (quickAction, startInStageIndex) => {
      if (quickAction.scvTimedForce) {
        return new ScvTimedForceViewModel(
          {
            type: "scv",
            scvInputId: quickAction.scvTimedForce.scvInputId,
          },
          startInStageIndex,
          durationInSecondsOrZero(quickAction.startDelay),
          quickAction.actionId,
          {
            scvInputId: quickAction.scvTimedForce.scvInputId,
            direction:
              Math.sign(quickAction.scvTimedForce.forcePct) >= 0 ? 1 : -1,
            forcePct: Math.abs(quickAction.scvTimedForce.forcePct),
            durationSec: durationInSecondsOrZero(
              quickAction.scvTimedForce.duration
            ),
          }
        );
      }
    },
    components: {
      Icon: TimedForceIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const { measurementSystem } = useSelf();
        const directionLabelId = useRandomId();
        const forceLabelId = useRandomId();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormControl fullWidth className="col-span-full">
              <FormLabel id={directionLabelId}>
                {t(
                  "views.autotractor.autonomousActivities.action.parameterName.direction"
                )}
              </FormLabel>
              <DirectionToggle
                variant="scv"
                basePath={basePath.actionData.direction}
                aria-labelledby={directionLabelId}
              />
            </FormControl>
            <FormikNumberInput
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.force"
              )}
              name={basePath.actionData.forcePct.$path}
              numberInputProps={{
                min: 0,
                max: 100,
                textFieldProps: {
                  "aria-labelledby": forceLabelId,
                  slotProps: {
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {t("utils.units.%")}
                        </InputAdornment>
                      ),
                    },
                  },
                },
              }}
            />
            <FormikDimensionedNumberInput
              name={basePath.actionData.durationSec.$path}
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.duration"
              )}
              dimensionedNumberInputProps={{
                minStoredValue: 0,
                storedUnit: "s",
                localSystem: measurementSystem,
                displayUnits: { imperial: "s", metric: "s" },
              }}
              className="col-start-1"
            />
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };
//#endregion

//#region SCV float
class ScvFloatViewModel extends ActionViewModel<ScvFloatViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string,
    public actionData: ScvFloat
  ) {
    super(effector, true, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      scvFloat: this.actionData,
      carryOverIntoWorkTask: true,
    };
  }

  override actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex >= this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.scvFloat",
        {
          function: functionNameForScvInputId(
            t,
            rigEffectiveConfig,
            this.actionData.scvInputId
          ),
        }
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.float"
    );
  }

  override components = scvFloat.components;
}
export const scvFloat: ActionViewModelImplementation<ScvFloatViewModel> = {
  actionButtonLabelI18nKey:
    "views.autotractor.autonomousActivities.action.buttonLabel.float",
  actionMenuSection: "holding-action",
  isNewActionAvailableForEffector: allEffectorsWithType("scv"),
  newAction: (effector, startInStageIndex) => {
    if (effector.type !== "scv") {
      throw new Error('scvFloat.newAction: effector type is not "scv"');
    }

    return new ScvFloatViewModel(effector, startInStageIndex, 0, "", {
      scvInputId: effector.scvInputId,
    });
  },
  fromQuickAction: (quickAction, startInStageIndex) => {
    if (quickAction.scvFloat) {
      return new ScvFloatViewModel(
        {
          type: "scv",
          scvInputId: quickAction.scvFloat.scvInputId,
        },
        startInStageIndex,
        durationInSecondsOrZero(quickAction.startDelay),
        quickAction.actionId,
        quickAction.scvFloat
      );
    }
  },
  components: {
    Icon: FloatIcon,
    EditForm: ({ basePath }) => {
      return (
        <div className="grid grid-cols-2 gap-2">
          <StartDelayInput path={basePath.startDelaySec} />
        </div>
      );
    },
  },
};
//#endregion

//#region SCV constant pressure
class ScvConstantPressureViewModel extends ActionViewModel<ScvConstantPressureViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string,
    public actionData: {
      scvInputId: string;
      // currently only +/- 100 force are supported
      direction: 1 | -1;
    }
  ) {
    super(effector, true, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      scvConstantForce: {
        scvInputId: this.actionData.scvInputId,
        forcePct: this.actionData.direction > 0 ? 100 : -100,
      },
      carryOverIntoWorkTask: true,
    };
  }
  override actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex >= this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.scvConstantPressure",
        {
          function: functionNameForScvInputId(
            t,
            rigEffectiveConfig,
            this.actionData.scvInputId
          ),
        }
      );
    }
  }
  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.constantPressure"
    );
  }
  override components = scvConstantPressure.components;
}
export const scvConstantPressure: ActionViewModelImplementation<ScvConstantPressureViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.constantPressure",
    actionMenuSection: "holding-action",
    isNewActionAvailableForEffector: allEffectorsWithType("scv"),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "scv") {
        throw new Error(
          'scvConstantPressure.newAction: effector type is not "scv"'
        );
      }

      return new ScvConstantPressureViewModel(
        effector,
        startInStageIndex,
        0,
        "",
        {
          scvInputId: effector.scvInputId,
          direction: 1,
        }
      );
    },
    fromQuickAction: (action, startInStageIndex) => {
      if (action.scvConstantForce) {
        return new ScvConstantPressureViewModel(
          {
            type: "scv",
            scvInputId: action.scvConstantForce.scvInputId,
          },
          startInStageIndex,
          durationInSecondsOrZero(action.startDelay),
          action.actionId,
          {
            scvInputId: action.scvConstantForce.scvInputId,
            direction:
              Math.sign(action.scvConstantForce.forcePct) >= 0 ? 1 : -1,
          }
        );
      }
    },
    components: {
      Icon: ConstantPressureIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const directionLabelId = useRandomId();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormControl fullWidth className="col-span-full">
              <FormLabel id={directionLabelId}>
                {t(
                  "views.autotractor.autonomousActivities.action.parameterName.direction"
                )}
              </FormLabel>
              <DirectionToggle
                basePath={basePath.actionData.direction}
                variant="scv"
                aria-labelledby={directionLabelId}
              />
            </FormControl>
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };
//#endregion
